import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import type { JobListItem } from '../types/api'
import { JOB_STATUSES, JOB_TYPES } from '../types/api'
import Button from '../components/ui/Button'
import Badge from '../components/ui/Badge'
import Modal from '../components/ui/Modal'
import FileUpload from '../components/ui/FileUpload'

export function JobStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    'Draft':        'bg-surface-lowest text-text-muted border border-border-subtle',
    'Open':         'bg-primary-light text-primary',
    'On Hold':      'bg-accent-warning-light text-accent-warning',
    'Interviewing': 'bg-blue-50 text-blue-700',
    'Filled':       'bg-accent-success-light text-accent-success',
    'Closed':       'bg-surface-lowest text-text-faint border border-border-subtle',
  }
  const cls = styles[status] ?? 'bg-surface-lowest text-text-muted border border-border-subtle'
  return <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${cls}`}>{status}</span>
}

export function JobTypeBadge({ type }: { type: string }) {
  const styles: Record<string, string> = {
    'Exclusive': 'bg-primary-light text-primary font-medium',
    'Listed':    'text-text-muted',
    'Imported':  'text-text-muted',
  }
  const cls = styles[type] ?? 'text-text-muted'
  return <span className={`text-xs ${cls}`}>{type}</span>
}

type BasicForm = {
  title: string
  employer: string
  location: string
  employment_type: string
  job_duration: string
  working_languages: string
  salary_range: string
  positions: string
  application_deadline: string
  source_url: string
  source_id: string
  description: string
  status: string
  type: string
  required_topics: string
  preferred_topics: string
}

const EMPTY_FORM: BasicForm = {
  title: '', employer: '', location: '', employment_type: '',
  job_duration: '', working_languages: '', salary_range: '', positions: '', application_deadline: '',
  source_url: '', source_id: '',
  description: '', status: 'Draft', type: 'Exclusive', required_topics: '', preferred_topics: '',
}

export default function Jobs() {
  const navigate = useNavigate()
  const [jobs, setJobs] = useState<JobListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showUpload, setShowUpload] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState<BasicForm>(EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const load = () =>
    api.jobs.list().then((r) => setJobs(r.data)).catch((e) => setError(e.message)).finally(() => setLoading(false))

  useEffect(() => { load() }, [])

  const handleUpload = async (file: File) => {
    await api.jobs.uploadFile(file)
    setShowUpload(false)
    load()
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title.trim() || !form.employer.trim()) { setFormError('Title and employer are required'); return }
    setFormError(null)
    setSubmitting(true)
    try {
      const required_topics = form.required_topics.split(',').map((s) => s.trim()).filter(Boolean)
      const preferred_topics = form.preferred_topics.split(',').map((s) => s.trim()).filter(Boolean)
      const working_languages = form.working_languages.split(',').map((s) => s.trim()).filter(Boolean)
      const positions = form.positions ? parseInt(form.positions, 10) : undefined
      await api.jobs.createBasic({
        title: form.title,
        employer: form.employer,
        ...(form.location && { location: form.location }),
        ...(form.employment_type && { employment_type: form.employment_type }),
        ...(form.job_duration && { job_duration: form.job_duration }),
        ...(working_languages.length && { working_languages }),
        ...(form.salary_range && { salary_range: form.salary_range }),
        ...(positions && { positions }),
        ...(form.application_deadline && { application_deadline: form.application_deadline }),
        ...(form.source_url && { source_url: form.source_url }),
        ...(form.source_id && { source_id: form.source_id }),
        ...(form.description && { description: form.description }),
        status: form.status,
        type: form.type,
        ...(required_topics.length && { required_topics }),
        ...(preferred_topics.length && { preferred_topics }),
      })
      setShowCreate(false)
      setForm(EMPTY_FORM)
      load()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to create job')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-text-muted tracking-tactical mb-1">OPERATIONS</p>
          <h1 className="text-2xl">JOBS</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => setShowUpload(true)}>UPLOAD JOB FILE</Button>
          <Button variant="primary" onClick={() => setShowCreate(true)}>ADD JOB</Button>
        </div>
      </div>

      {error && <p className="text-red-400 text-sm">{error}</p>}

      {loading ? (
        <p className="text-text-muted text-sm">Loading jobs...</p>
      ) : jobs.length === 0 ? (
        <div className="border border-dashed border-border-subtle p-12 text-center">
          <p className="text-text-muted text-sm">No jobs yet. Upload a job posting or add manually.</p>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-subtle">
              <th className="text-left text-xs text-text-muted tracking-tactical py-3 pr-4">TITLE</th>
              <th className="text-left text-xs text-text-muted tracking-tactical py-3 pr-4">EMPLOYER</th>
              <th className="text-left text-xs text-text-muted tracking-tactical py-3 pr-4">STATUS</th>
              <th className="text-left text-xs text-text-muted tracking-tactical py-3 pr-4">TYPE</th>
              <th className="text-left text-xs text-text-muted tracking-tactical py-3">REQUIRED TOPICS</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr
                key={j.id}
                onClick={() => navigate(`/admin/jobs/${j.id}`)}
                className="border-b border-border-subtle cursor-pointer hover:bg-surface-low transition-colors"
              >
                <td className="py-3 pr-4 font-medium">{j.title}</td>
                <td className="py-3 pr-4 text-text-muted">{j.employer}</td>
                <td className="py-3 pr-4">
                  <JobStatusBadge status={j.status} />
                </td>
                <td className="py-3 pr-4">
                  <JobTypeBadge type={j.type} />
                </td>
                <td className="py-3">
                  <div className="flex flex-wrap gap-1">
                    {j.required_topics.slice(0, 5).map((t) => (
                      <Badge key={t} variant="required">{t}</Badge>
                    ))}
                    {j.required_topics.length > 5 && (
                      <Badge variant="muted">+{j.required_topics.length - 5}</Badge>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal isOpen={showUpload} onClose={() => setShowUpload(false)} title="UPLOAD JOB FILE">
        <FileUpload
          onUpload={handleUpload}
          label="Upload job posting (PDF, TXT, MD, DOC)"
        />
      </Modal>

      <Modal isOpen={showCreate} onClose={() => { setShowCreate(false); setForm(EMPTY_FORM); setFormError(null) }} title="ADD JOB">
        <form onSubmit={handleCreate} className="space-y-3">
          {formError && <p className="text-red-400 text-xs">{formError}</p>}
          {[
            { key: 'title', label: 'JOB TITLE *', placeholder: 'Senior Backend Developer' },
            { key: 'employer', label: 'EMPLOYER *', placeholder: 'TechCorp' },
            { key: 'location', label: 'LOCATION', placeholder: 'Helsinki, Finland (Hybrid)' },
            { key: 'employment_type', label: 'EMPLOYMENT TYPE', placeholder: 'Full-time' },
            { key: 'job_duration', label: 'DURATION', placeholder: '3-6 months / permanent' },
            { key: 'working_languages', label: 'WORKING LANGUAGES (comma-separated)', placeholder: 'Finnish, English' },
            { key: 'salary_range', label: 'SALARY RANGE', placeholder: '4000-5000 €/month' },
            { key: 'positions', label: 'POSITIONS', placeholder: '1', type: 'number' },
            { key: 'application_deadline', label: 'APPLICATION DEADLINE', placeholder: 'YYYY-MM-DD' },
            { key: 'source_url', label: 'SOURCE URL', placeholder: 'https://example.com/job' },
            { key: 'source_id', label: 'SOURCE ID', placeholder: 'External job ID' },
            { key: 'required_topics', label: 'REQUIRED TOPICS (comma-separated)', placeholder: 'TypeScript, Node.js, PostgreSQL' },
            { key: 'preferred_topics', label: 'PREFERRED TOPICS (comma-separated)', placeholder: 'Docker, Kubernetes, AWS' },
          ].map(({ key, label, placeholder, type = 'text' }) => (
            <div key={key}>
              <label className="block text-xs text-text-muted tracking-tactical mb-1">{label}</label>
              <input
                type={type}
                value={form[key as keyof BasicForm]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                placeholder={placeholder}
                className="w-full bg-background border border-border-subtle text-foreground text-sm px-3 py-2 focus:outline-none focus:border-primary"
              />
            </div>
          ))}
          <div>
            <label className="block text-xs text-text-muted tracking-tactical mb-1">STATUS</label>
            <select
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              className="w-full bg-background border border-border-subtle text-foreground text-sm px-3 py-2 focus:outline-none focus:border-primary"
            >
              {JOB_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-text-muted tracking-tactical mb-1">TYPE</label>
            <select
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              className="w-full bg-background border border-border-subtle text-foreground text-sm px-3 py-2 focus:outline-none focus:border-primary"
            >
              {JOB_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-text-muted tracking-tactical mb-1">DESCRIPTION</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Role description..."
              rows={3}
              className="w-full bg-background border border-border-subtle text-foreground text-sm px-3 py-2 focus:outline-none focus:border-primary resize-none"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={submitting}>{submitting ? 'CREATING...' : 'CREATE JOB'}</Button>
            <Button variant="ghost" type="button" onClick={() => { setShowCreate(false); setForm(EMPTY_FORM) }}>CANCEL</Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
